import { SegmentModel } from '../../models/segments/segment.model'
import { OrgSegmentModel } from '../../models/segments/org-segment.model'
import { getSegmentCreateDto, getSegmentUpdateDto } from './segment-dto'

export type AnySegment = SegmentModel | OrgSegmentModel

export interface SegmentSaveArgs {
    segment: AnySegment
    original?: AnySegment
    /**
     * Present only for org-level segments, where the set of domains the segment
     * spans is part of the payload. Its presence is what selects the org-level
     * DTO shape.
     */
    domainIds?: number[]
}

export interface SegmentSavePlan {
    mode: 'create' | 'update'
    dto: Record<string, unknown>
}

/**
 * Builds the persistence payload for a segment.
 *
 * The DTO helpers are shared with the other segment surfaces and are reused
 * unchanged so the wire shape — and the server-side hash the API derives from
 * `filters_json` — stays identical across all of them. `filters_hash` is never
 * part of the payload: only the server may compute it.
 *
 * Two things are restored afterwards, both to keep the shared helper untouched
 * for its other callers. The record id, because the update helper assigns it only
 * after diffing name, icon and filters and before diffing channels and domains,
 * so a change to just channels or just domains would produce an id-less payload.
 * And the org-level domain set, because the org endpoints take it as part of the
 * segment on every write while the diff emits it only when it changed.
 */
export function buildSegmentSaveDto({ segment, original, domainIds }: SegmentSaveArgs): SegmentSavePlan {
    if (!original) {
        const dto =
            domainIds !== undefined
                ? getSegmentCreateDto(segment as OrgSegmentModel, domainIds)
                : getSegmentCreateDto(segment as SegmentModel)

        return { mode: 'create', dto: dto as unknown as Record<string, unknown> }
    }

    const dto = (
        domainIds !== undefined
            ? getSegmentUpdateDto(segment as OrgSegmentModel, original as OrgSegmentModel, domainIds)
            : getSegmentUpdateDto(segment as SegmentModel, original as SegmentModel)
    ) as unknown as Record<string, unknown>

    if (Object.keys(dto).length > 0 && dto.id === undefined) {
        dto.id = segment.getId()
    }

    // Restored only onto a payload that already carries a change, so both things
    // stay true: any request that goes out states the domains, and a segment
    // nobody edited still produces a payload holding nothing to send.
    if (domainIds !== undefined && hasSaveChanges(dto)) {
        dto.domain_ids = domainIds
    }

    return { mode: 'update', dto }
}

/**
 * True when an update DTO carries an actual change.
 *
 * The id is an addressing field rather than a change, so a DTO holding nothing
 * else describes a segment nobody edited and needs no request.
 */
export function hasSaveChanges(dto: Record<string, unknown>): boolean {
    return Object.keys(dto).some((key) => key !== 'id')
}

/**
 * Normalizes the two segment services' differing success conventions.
 *
 * The org-level service resolves a response envelope carrying `ok`; the
 * domain-level one resolves the saved record itself, or `undefined` when the
 * request failed. Both raise their own notifications either way, so callers use
 * this only to decide what to do next — never to decide what to tell the user.
 */
export function didSaveSucceed(result: unknown): boolean {
    if (result == null) return false

    if (isResponseEnvelope(result)) {
        return !!result.ok
    }

    return true
}

/**
 * The record id a save resolved with, when the result states one.
 *
 * This is the whole of what a save path may take from a response, and a create
 * genuinely needs it: it is the only place the new record's id exists, and
 * without it a second save from the same mounted builder has no id to address
 * and would create a duplicate.
 *
 * Everything else a response happens to carry is ignored, because nothing
 * obliges a write to answer with the record it stored — an answer may be partial
 * or absent. A caller that adopted it as its saved state would make its own
 * correctness depend on how much the service chose to echo.
 */
export function getResponseRecordId(result: unknown): number | undefined {
    return toSegmentId(getResponseRecord(result)?.id)
}

/**
 * A segment id in the one form the rest of the code can use, or nothing.
 *
 * Both models type the id as a number and both services take it as one — in the
 * path a domain-level write PATCHes, and in the identifier object the org-level
 * write names the segment by. So an id JSON stated as a string is converted here
 * rather than passed along: a string travelling under a `number` type reaches the
 * services only because nothing checks, and reads back out of a model as a value
 * that is not what it claims to be.
 *
 * Whole numbers only, and only ones that survive the trip: a fractional or
 * out-of-range value does not name a record, and rounding it would name the wrong
 * one. Anything else — absent, blank, non-numeric — is no id at all and comes back
 * undefined, which is a caller's cue to refuse rather than to address nothing.
 */
export function toSegmentId(value: unknown): number | undefined {
    if (typeof value === 'number') return Number.isSafeInteger(value) ? value : undefined
    if (typeof value !== 'string' || value.trim() === '') return undefined

    const parsed = Number(value)

    return Number.isSafeInteger(parsed) ? parsed : undefined
}

/**
 * The segment record out of either service's result, whether that result is a
 * response envelope or the record itself.
 */
export function getResponseRecord(result: unknown): Record<string, unknown> | undefined {
    if (result == null) return undefined

    const record = isResponseEnvelope(result) ? result.data : result

    return record && typeof record === 'object' ? (record as Record<string, unknown>) : undefined
}

function isResponseEnvelope(result: unknown): result is { ok?: boolean; data?: unknown } {
    return typeof result === 'object' && result !== null && 'ok' in (result as Record<string, unknown>)
}
